import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Shield, Check, X, Pen, RotateCcw, AlertTriangle, MapPin, Phone, CheckCircle2, Download } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function InvoiceWarrantySignPage() {
  const { invoiceId, token } = useParams();
  const navigate = useNavigate();
  
  const [step, setStep] = useState('loading'); // loading, otp, conditions, signed, error, already_signed
  const [pageData, setPageData] = useState(null);
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [signatureData, setSignatureData] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  
  const canvasRef = useRef(null);

  // Charger les données de la page
  useEffect(() => {
    const loadPageData = async () => {
      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/invoices/warranty/sign/${invoiceId}/${token}`
        );
        
        if (response.data.already_signed) {
          setPageData(response.data);
          setStep('already_signed');
        } else {
          setPageData(response.data);
          setStep('otp');
        }
      } catch (err) {
        console.error('Error loading page:', err);
        setError(err.response?.data?.detail || 'Lien invalide ou expiré');
        setStep('error');
      }
    };
    
    loadPageData();
  }, [invoiceId, token]);

  // Initialiser le canvas
  useEffect(() => {
    if (step === 'conditions' && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#1e40af';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, [step]);

  const startDrawing = (e) => {
    if (!canvasRef.current) return;
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing || !canvasRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    if (canvasRef.current) {
      setSignatureData(canvasRef.current.toDataURL('image/png'));
    }
  };

  const clearCanvas = () => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setSignatureData(null);
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      toast.error('Le code OTP doit contenir 6 chiffres');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/invoices/${invoiceId}/warranty/verify-otp`,
        null,
        { params: { otp, token } }
      );
      
      if (response.data.verified) {
        setStep('conditions');
      }
    } catch (err) {
      console.error('OTP verification error:', err);
      toast.error(err.response?.data?.detail || 'Code OTP incorrect');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSign = async () => {
    if (!signatureData) {
      toast.error('Veuillez signer avant de valider');
      return;
    }

    // Vérifier que l'OTP est bien stocké
    if (!otp || otp.length !== 6) {
      toast.error('Code OTP invalide. Veuillez recharger la page et réessayer.');
      return;
    }

    setIsLoading(true);
    try {
      console.log('Sending signature request...', {
        url: `${BACKEND_URL}/api/invoices/warranty/sign/${invoiceId}/${token}`,
        otpLength: otp?.length,
        signatureLength: signatureData?.length
      });
      
      const response = await axios.post(
        `${BACKEND_URL}/api/invoices/warranty/sign/${invoiceId}/${token}`,
        { otp: otp, signature: signatureData },
        { 
          headers: { 'Content-Type': 'application/json' },
          timeout: 30000 // 30 secondes timeout
        }
      );
      
      console.log('Signature response:', response.data);
      
      if (response.data.success) {
        // Stocker l'URL de téléchargement si disponible
        if (response.data.download_url) {
          setDownloadUrl(response.data.download_url);
        }
        setStep('signed');
        toast.success('Signature enregistrée avec succès !');
      }
    } catch (err) {
      console.error('Signature error full:', err);
      console.error('Response data:', err.response?.data);
      console.error('Response status:', err.response?.status);
      
      // Afficher l'erreur détaillée
      let errorMessage = 'Erreur lors de la signature';
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message === 'Network Error') {
        errorMessage = 'Erreur réseau. Vérifiez votre connexion internet.';
      } else if (err.code === 'ECONNABORTED') {
        errorMessage = 'Délai d\'attente dépassé. Réessayez.';
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Page de chargement
  if (step === 'loading') {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Chargement...</p>
        </div>
      </div>
    );
  }

  // Page d'erreur
  if (step === 'error') {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-slate-800 mb-2">Lien invalide</h1>
          <p className="text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  // Déjà signé
  if (step === 'already_signed') {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-xl font-bold text-slate-800 mb-2">Déjà signé</h1>
          <p className="text-slate-600 mb-4">
            Cette facture a déjà été signée le {pageData?.signature_date?.slice(0, 10)}.
          </p>
          <p className="text-sm text-slate-500 mb-4">
            Client: {pageData?.client_name}<br />
            Facture: {pageData?.invoice_number}
          </p>
          
          {/* Bouton de téléchargement */}
          {pageData?.download_url && (
            <a 
              href={pageData.download_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
            >
              <Download className="w-5 h-5" />
              Télécharger ma facture signée
            </a>
          )}
        </div>
      </div>
    );
  }

  // Signature réussie
  if (step === 'signed') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-green-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-green-800 mb-2">Signature enregistrée !</h1>
          <p className="text-slate-600 mb-6">
            Merci d'avoir signé les conditions de garantie pour votre facture.
          </p>
          <div className="bg-green-50 rounded-lg p-4 text-left mb-6">
            <p className="text-sm text-green-800">
              <strong>Facture:</strong> {pageData?.invoice_number}<br />
              <strong>Client:</strong> {pageData?.client_name}<br />
              <strong>Montant:</strong> {pageData?.total?.toFixed(2)} €
            </p>
          </div>
          
          {/* Bouton de téléchargement */}
          {downloadUrl && (
            <a 
              href={downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-6 rounded-lg transition-colors mb-4"
            >
              <Download className="w-5 h-5" />
              Télécharger ma facture signée
            </a>
          )}
          
          <p className="text-sm text-slate-500">
            {downloadUrl ? 'Vous pouvez télécharger votre facture à tout moment depuis ce lien.' : 'Vous pouvez fermer cette page.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-slate-100">
      {/* Header */}
      <div className="bg-blue-900 text-white py-6 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <Shield className="w-12 h-12 mx-auto mb-3 text-blue-300" />
          <h1 className="text-2xl font-bold">PhoneLab</h1>
          <p className="text-blue-200 text-sm mt-1">Signature des conditions de garantie</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 py-8">
        {/* Informations facture */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h2 className="font-bold text-lg text-slate-800 mb-4">
            Facture: {pageData?.invoice_number}
          </h2>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-slate-500">Client:</span>
              <p className="font-medium">{pageData?.client_name}</p>
            </div>
            <div>
              <span className="text-slate-500">Montant:</span>
              <p className="font-medium">{pageData?.total?.toFixed(2)} €</p>
            </div>
          </div>
          {pageData?.items?.length > 0 && (
            <div className="mt-4 pt-4 border-t">
              <span className="text-slate-500 text-sm">Articles:</span>
              <ul className="text-sm mt-1">
                {pageData.items.map((item, i) => (
                  <li key={i} className="text-slate-700">• {item.description}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Étape OTP */}
        {step === 'otp' && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-amber-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-800">Vérification</h2>
              <p className="text-slate-600 mt-2">
                Entrez le code OTP reçu par SMS ou email
              </p>
            </div>

            <div className="max-w-xs mx-auto">
              <Input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="text-center text-3xl font-mono tracking-widest h-16"
                maxLength={6}
              />
              
              <Button 
                onClick={handleVerifyOtp}
                disabled={otp.length !== 6 || isLoading}
                className="w-full mt-4 h-12 bg-blue-600 hover:bg-blue-700"
              >
                {isLoading ? 'Vérification...' : 'Vérifier le code'}
              </Button>
            </div>
          </div>
        )}

        {/* Étape conditions + signature */}
        {step === 'conditions' && (
          <>
            {/* Conditions de garantie */}
            <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
              <h2 className="font-bold text-lg text-blue-900 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                CONDITIONS DE GARANTIE
              </h2>
              
              <div className="space-y-4 text-sm">
                {/* 1. Référentiel */}
                <div className="bg-green-50 border border-green-200 rounded p-3">
                  <h4 className="font-bold text-green-800 mb-2">1. RÉFÉRENTIEL DES GARANTIES</h4>
                  <ul className="text-green-700 space-y-1">
                    <li>• Appareils Neufs / Reconditionnés : <strong>24 mois</strong></li>
                    <li>• Pièces d'Origine (Service Pack) : <strong>12 mois</strong></li>
                    <li>• Compatibles Premium : <strong>6 mois</strong></li>
                    <li>• Entrée de Gamme : <strong>3 mois minimum</strong></li>
                  </ul>
                  <p className="text-green-600 mt-2 text-xs">
                    ⚖️ Art. L217-11 : Immobilisation &gt; 7 jours = Extension automatique
                  </p>
                </div>

                {/* 2. Pris en charge */}
                <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
                  <h4 className="font-bold text-emerald-800 mb-2">2. CE QUI EST PRIS EN CHARGE</h4>
                  <p className="text-emerald-700">
                    Vices de fabrication et défauts internes uniquement :
                    Affichage (pixels morts sans choc), Tactile (zones mortes), 
                    Batterie (gonflement, perte capacité), Composants (boutons, nappe)
                  </p>
                </div>

                {/* 3. Exclusions */}
                <div className="bg-red-50 border-2 border-red-300 rounded p-3">
                  <h4 className="font-bold text-red-800 mb-2">3. CE QUI EST EXCLU (REJET)</h4>
                  <ul className="text-red-600 space-y-1">
                    <li><strong>A. Écran :</strong> Lignes colorées, taches noires, écran blanc/noir, fissure</li>
                    <li><strong>B. Physique :</strong> Vitre brisée/ébréchée, châssis tordu, coins enfoncés</li>
                    <li><strong>C. Intervention Tierce :</strong> Vis marquées/manquantes, sticker rompu, pièce démontée</li>
                    <li><strong>D. Oxydation :</strong> Pastilles rouges/roses, calcaire/corrosion, pins tordus</li>
                  </ul>
                </div>

                {/* 4. Clauses juridiques */}
                <div className="bg-slate-100 border border-slate-300 rounded p-3">
                  <h4 className="font-bold text-slate-800 mb-2">4. CLAUSES JURIDIQUES</h4>
                  <ul className="text-slate-700 space-y-1">
                    <li>📜 <strong>Exclusivité Technique :</strong> Garantie caduque si ouverture par tiers, choc, oxydation. Pièce DOIT rester montée.</li>
                    <li>📜 <strong>Responsabilité Données (Art. 1231-1) :</strong> Client responsable de la sauvegarde.</li>
                    <li>📜 <strong>Droit de Rétention (Art. 2286) :</strong> Appareil conservé jusqu'au paiement.</li>
                  </ul>
                </div>

                {/* 5. Logique système */}
                <div className="bg-purple-50 border border-purple-200 rounded p-3">
                  <h4 className="font-bold text-purple-800 mb-2">5. LOGIQUE SYSTÈME</h4>
                  <ul className="text-purple-700 space-y-1">
                    <li>📸 3 photos obligatoires (Face, Dos, Profil) à l'entrée et sortie</li>
                    <li>✍️ Signature électronique obligatoire à chaque étape</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Zone de signature */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-slate-800 flex items-center gap-2">
                  <Pen className="w-5 h-5 text-blue-600" />
                  Votre signature
                </h3>
                <Button variant="outline" size="sm" onClick={clearCanvas}>
                  <RotateCcw className="w-4 h-4 mr-1" /> Effacer
                </Button>
              </div>
              
              <canvas
                ref={canvasRef}
                width={600}
                height={180}
                className="w-full border-2 border-slate-300 rounded-lg cursor-crosshair bg-white touch-none"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
              
              <p className="text-xs text-slate-500 mt-3 text-center">
                En signant, je confirme avoir lu et accepté les conditions de garantie ci-dessus.
              </p>

              <Button 
                onClick={handleSign}
                disabled={!signatureData || isLoading}
                className="w-full mt-4 h-12 bg-green-600 hover:bg-green-700"
              >
                {isLoading ? 'Envoi en cours...' : (
                  <>
                    <Check className="w-5 h-5 mr-2" />
                    Signer et valider
                  </>
                )}
              </Button>
            </div>

            {/* Infos boutique */}
            {pageData?.shop_info && (
              <div className="bg-slate-50 rounded-lg p-4 mt-6 text-center text-sm text-slate-600">
                <p className="flex items-center justify-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {pageData.shop_info.address}
                </p>
                <p className="flex items-center justify-center gap-2 mt-1">
                  <Phone className="w-4 h-4" />
                  {pageData.shop_info.phone}
                </p>
                <a 
                  href={pageData.shop_info.maps_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline mt-2 inline-block"
                >
                  Voir sur Google Maps
                </a>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
